import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { DatadogProvider } from '@datadog/openfeature-browser';
import { EvaluationContext, OpenFeature } from '@openfeature/web-sdk';
import { markFeatureFlagsSettled } from '../lib/featureFlags';

type PrivacyLevel = 'mask-user-input' | 'mask' | 'allow' | undefined;

const DD_SITES = [
  'datadoghq.com',
  'datadoghq.eu',
  'us3.datadoghq.com',
  'us5.datadoghq.com',
] as const;

type Site = (typeof DD_SITES)[number];

function assertIsDdSite(value: string): asserts value is Site {
  if (!(DD_SITES as readonly string[]).includes(value)) {
    throw new Error(`Invalid Datadog site: ${value}`);
  }
}

const datadogSite = import.meta.env.VITE_DATADOG_SITE || 'datadoghq.com';
assertIsDdSite(datadogSite);

// DataDog RUM configuration
const DATADOG_CONFIG = {
  applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID || '',
  clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN || '',
  site: datadogSite,
  service: import.meta.env.VITE_DATADOG_SERVICE || 'nova-web-application',
  env:
    import.meta.env.VITE_DATADOG_ENV || import.meta.env.MODE || 'development',
  version: import.meta.env.VITE_VERSION || '1.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input' as PrivacyLevel,
  startSessionReplayRecordingManually: true,
};

export const initDatadog = () => {
  // Only initialize if we have the required configuration
  if (!DATADOG_CONFIG.applicationId || !DATADOG_CONFIG.clientToken) {
    console.warn(
      'DataDog RUM not initialized: Missing applicationId or clientToken'
    );
    return;
  }

  datadogRum.init({
    applicationId: DATADOG_CONFIG.applicationId,
    clientToken: DATADOG_CONFIG.clientToken,
    site: DATADOG_CONFIG.site,
    service: DATADOG_CONFIG.service,
    env: DATADOG_CONFIG.env,
    version: DATADOG_CONFIG.version,
    sessionSampleRate: DATADOG_CONFIG.sessionSampleRate,
    sessionReplaySampleRate: DATADOG_CONFIG.sessionReplaySampleRate,
    startSessionReplayRecordingManually:
      DATADOG_CONFIG.startSessionReplayRecordingManually,
    trackUserInteractions: DATADOG_CONFIG.trackUserInteractions,
    trackResources: DATADOG_CONFIG.trackResources,
    trackLongTasks: DATADOG_CONFIG.trackLongTasks,
    defaultPrivacyLevel: DATADOG_CONFIG.defaultPrivacyLevel,
    plugins: [reactPlugin({ router: true })],
  });

  // Note: Session replay recording is started conditionally via startSessionReplayIfExternal()
  // based on user email to exclude internal Matterworks users

  console.log('DataDog RUM initialized successfully');
};

let featureFlagsInitStarted = false;

// Register the OpenFeature Datadog provider together with the user's
// evaluation context, so it only ever reports ready with the real user's
// targeting in place — flags are never evaluated against an anonymous context.
export const initFeatureFlagsForUser = async (email?: string) => {
  if (featureFlagsInitStarted) {
    return;
  }
  featureFlagsInitStarted = true;

  const hasDatadogCredentials =
    Boolean(DATADOG_CONFIG.applicationId) &&
    Boolean(DATADOG_CONFIG.clientToken);

  try {
    if (!hasDatadogCredentials) {
      console.warn(
        'OpenFeature: no Datadog credentials; flags use registry defaults.'
      );
      return;
    }

    const evaluationContext: EvaluationContext = email
      ? {
          targetingKey: email,
          email,
          email_domain: email.split('@')[1]?.toLowerCase() ?? '',
        }
      : {};

    await OpenFeature.setProviderAndWait(
      new DatadogProvider({
        applicationId: DATADOG_CONFIG.applicationId,
        clientToken: DATADOG_CONFIG.clientToken,
        site: DATADOG_CONFIG.site,
        env: DATADOG_CONFIG.env,
      }),
      evaluationContext
    );
  } catch (error) {
    console.warn(
      'OpenFeature: provider initialization failed; flags use registry defaults.',
      error
    );
  } finally {
    markFeatureFlagsSettled();
  }
};

// Start session replay only for non-Matterworks email addresses
export const startSessionReplayIfExternal = (email?: string) => {
  if (!email) {
    return;
  }

  const domain = email.split('@')[1] || '';
  const isMatterworksEmail = domain.toLowerCase().includes('matterworks');

  if (!isMatterworksEmail) {
    datadogRum.startSessionReplayRecording();
    console.log('DataDog Session Replay started for external user');
  } else {
    console.log('DataDog Session Replay skipped for internal Matterworks user');
  }
};

// Utility functions for custom tracking
export const trackCustomEvent = (
  name: string,
  properties?: Record<string, any>
) => {
  datadogRum.addAction(name, properties);
};

export const trackCustomError = (
  error: Error,
  context?: Record<string, any>
) => {
  datadogRum.addError(error, context);
};

export const reportApplicationError = (
  error: unknown,
  componentStack?: string | null
) => {
  const applicationError =
    error instanceof Error
      ? error
      : new Error(error == null ? 'Unknown application error' : String(error));
  trackCustomError(applicationError, {
    component_stack: componentStack,
  });
};

export const setUserContext = (user: {
  id: string;
  email?: string;
  name?: string;
  role?: string;
  [key: string]: any;
}) => {
  datadogRum.setUser(user);
};

export const addGlobalContext = (key: string, value: any) => {
  datadogRum.setGlobalContext({ [key]: value });
};

export const trackTiming = (name: string, duration: number) => {
  datadogRum.addTiming(name, duration);
};

export const startView = (name: string) => {
  datadogRum.startView(name);
};
